/**
 * Local (this-browser-only) history of completed import operations.
 *
 * The real backend has no history/audit-log READ endpoint for import
 * operations (only a write-only `recordAudit` call happens server-side —
 * verified against every apps/api/src controller). Rather than invent a
 * fake server-backed history screen, every entry here is a real result
 * from a real `POST .../crm/leads/import` call, just persisted in this
 * browser's `localStorage` instead of on the server — the UI labels it
 * "تاریخچهٔ این مرورگر" so it is never mistaken for a shared/server record.
 * Never throws: storage can be blocked (private browsing) or unavailable
 * outside a browser (SSR/tests), and the feature degrades to "no history"
 * rather than crashing the page.
 */
import { systemClock, toISOString } from '@sakhtar/calendar';

const STORAGE_KEY_PREFIX = 'sakhtar-data-import-history:';
const MAX_ENTRIES = 20;

export interface ImportHistoryEntry {
  readonly id: string;
  readonly domainLabel: string;
  readonly createdAt: string;
  readonly importedCount: number;
  readonly skippedCount: number;
  readonly errorCount: number;
}

function storageKey(slug: string): string {
  return `${STORAGE_KEY_PREFIX}${slug}`;
}

export function listImportHistory(slug: string): readonly ImportHistoryEntry[] {
  try {
    const raw = window.localStorage.getItem(storageKey(slug));
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as ImportHistoryEntry[]) : [];
  } catch {
    return [];
  }
}

export function recordImportHistory(
  slug: string,
  entry: Omit<ImportHistoryEntry, 'id' | 'createdAt'>,
): void {
  try {
    const existing = listImportHistory(slug);
    const next: ImportHistoryEntry[] = [
      { ...entry, id: crypto.randomUUID(), createdAt: toISOString(systemClock.now()) },
      ...existing,
    ].slice(0, MAX_ENTRIES);
    window.localStorage.setItem(storageKey(slug), JSON.stringify(next));
  } catch {
    // Best-effort only — a blocked/full storage must never break the import flow itself.
  }
}
